import { FLOOD_DEPTH_COLORS, type RGBAColor } from '@/config/colors';

/**
 * Fill cố định cho GeoJSON có `flood_visual_style: "photo_flat"` (mô phỏng kiểu ảnh: xanh nhạt bán trong suốt).
 * #ADD8E6 với alpha ~0.35 trên kênh 0–255.
 */
export const FLOOD_PHOTO_FLAT_FILL_RGBA: RGBAColor = [173, 216, 230, Math.round(255 * 0.35)];

/** Viền #1E90FF đặc (0–255). */
export const FLOOD_PHOTO_FLAT_STROKE_RGBA: RGBAColor = [30, 144, 255, 255];

/** Hệ số nhân alpha khi tô polygon GeoJSON vùng ngập (2D Deck) — đồng bộ cảm giác với legend. */
export const FLOOD_GEOJSON_FILL_ALPHA_MULT = 0.34;

/** Độ mờ viền polygon GeoJSON (0–255). */
export const FLOOD_GEOJSON_STROKE_ALPHA = 158;

/**
 * Cạnh ô lưới phân tích (m) — trùng `generateFloodGridData`, `GridCellLayer` và KPI diện tích ô.
 * Giá trị 5 m: mật độ điểm gấp 4 lần so với lưới 10 m (cùng phạm vi khi `n` ô mỗi cạnh được nhân đôi).
 */
export const FLOOD_GRID_CELL_SIZE_M = 5;

/**
 * Ngưỡng độ sâu tối thiểu (m) mặc định: ô có `depth_m` ≥ giá trị này được tính là ô ngập trong
 * cac tinh toan / KPI lien quan (khi UI cho phep chinh nguong).
 */
export const SPATIAL_ANALYSIS_FLOOD_DEPTH_THRESHOLD_DEFAULT_M = 0.1;

/** Phạm vi chỉnh ngưỡng trên UI (m). */
export const SPATIAL_ANALYSIS_FLOOD_DEPTH_THRESHOLD_MIN_M = 0;
export const SPATIAL_ANALYSIS_FLOOD_DEPTH_THRESHOLD_MAX_M = 2;
export const SPATIAL_ANALYSIS_FLOOD_DEPTH_THRESHOLD_STEP = 0.05;

/**
 * Giới hạn trên (m) của slider “ngưỡng hiển thị” trên lưới mô phỏng — không cắt dữ liệu gốc,
 * chỉ định nấc tối đa để kéo chọn; ô sâu hơn vẫn có trong dữ liệu khi kéo đến tối đa.
 */
export const FLOOD_DEPTH_DISPLAY_FILTER_MAX_M = 10;

/** Phóng độ cao mực nước trên bản đồ 3D (chỉ hiển thị, không đổi `depth_m` thực). */
export const FLOOD_HEIGHT_EXAGGERATION = 5;

const METERS_PER_DEG_LAT = 111_320;

/**
 * Nửa bề ngang ô theo độ vĩ (ô vuông ~{@link FLOOD_GRID_CELL_SIZE_M} m).
 */
export function floodCellHalfDegLat(): number {
  return FLOOD_GRID_CELL_SIZE_M / 2 / METERS_PER_DEG_LAT;
}

/**
 * Nửa bề ngang ô theo độ kinh tại một vĩ độ (ô vuông ~{@link FLOOD_GRID_CELL_SIZE_M} m).
 */
export function floodCellHalfDegLng(latitudeDeg: number): number {
  return (
    FLOOD_GRID_CELL_SIZE_M / 2 / (METERS_PER_DEG_LAT * Math.cos((latitudeDeg * Math.PI) / 180))
  );
}

function lerpRgba(a: RGBAColor, b: RGBAColor, t: number): RGBAColor {
  const u = Math.max(0, Math.min(1, t));
  return [
    Math.round(a[0] + (b[0] - a[0]) * u),
    Math.round(a[1] + (b[1] - a[1]) * u),
    Math.round(a[2] + (b[2] - a[2]) * u),
    Math.round(a[3] + (b[3] - a[3]) * u),
  ];
}

/**
 * Màu RGBA theo độ sâu ngập (m), dựa trên `FLOOD_DEPTH_COLORS` / legend 5 bậc.
 * Trong từng khoảng (vd. 0–0,5 m) **nội suy** giữa hai màu liền kề để ô lưới demo thấy đậm/nhạt theo `depth_m`.
 *
 * @param depthM — Độ sâu mét (giá trị ô hoặc điểm đo)
 * @returns [R, G, B, A] với A 0–255
 */
export function floodDepthRgba(depthM: number): RGBAColor {
  const d = Number.isFinite(depthM) ? Math.max(0, depthM) : 0;
  const c = FLOOD_DEPTH_COLORS;
  if (d > 3) return c[4];
  if (d > 2) return lerpRgba(c[3], c[4], d - 2);
  /**
   * Khoảng 1–2 m (legend): nội suy c[2]→c[3] (lam) với gamma < 1 để mức ~1–1,2 m đã chuyển tông rõ,
   * thay vì nội suy tuyến tính quá nhạt.
   */
  if (d > 1) {
    const u = Math.min(1, d - 1);
    const t = Math.pow(u, 0.52);
    return lerpRgba(c[2], c[3], t);
  }
  if (d > 0.5) return lerpRgba(c[1], c[2], (d - 0.5) / 0.5);
  // Nhiều ô demo cụm 0,05–0,25 m: dùng gamma < 1 để kéo dải màu (đậm/nhạt) rõ hơn cùng legend 0–0,5 m.
  const u = Math.min(1, d / 0.5);
  const t = Math.pow(u, 0.52);
  return lerpRgba(c[0], c[1], t);
}

/**
 * Màu fill polygon vùng ngập (2D `SolidPolygonLayer`): cùng thang {@link floodDepthRgba},
 * kèm {@link FLOOD_GEOJSON_FILL_ALPHA_MULT} và hệ số alpha theo độ sâu — vùng nông nhạt hơn,
 * vùng sâu đậm hơn (tương tự bản đồ rủi ro có nhiều sắc độ chồng lớp).
 *
 * @param depthM — Độ sâu mét (vùng sau hiệu chỉnh/timeline)
 */
export function floodRegionPolygonFillRgba(depthM: number): RGBAColor {
  const base = floodDepthRgba(depthM);
  const d = Number.isFinite(depthM) ? Math.max(0, depthM) : 0;
  const depthBoost = 0.55 + 0.45 * Math.min(1, d / 2.5);
  const a = Math.round(base[3] * FLOOD_GEOJSON_FILL_ALPHA_MULT * depthBoost);
  return [base[0], base[1], base[2], Math.min(255, a)];
}

/**
 * Độ sâu danh nghĩa (m) cho vùng ngập SAR trên preview MapLibre — cùng thang màu
 * {@link floodRegionPolygonFillRgba} như polygon lớp Flood trên bản đồ Deck.
 */
export const SAR_FLOOD_EXTENT_PREVIEW_NOMINAL_DEPTH_M = 0.68;

/** `RGBA` kênh 0–255 → chuỗi `rgba(...)` cho paint MapLibre. */
export function floodRgbColorToCssRgba(rgba: RGBAColor): string {
  const [r, g, b, a] = rgba;
  return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
}

/**
 * Kiểm tra độ sâu mô phỏng (m) có nằm trong khoảng ngưỡng hiển thị [min, max] (cận biên gồm).
 * Dùng chung cho lớp lưới Deck và bộ lọc Redux `floodDepthFilter`.
 */
export function isFloodDepthInDisplayRange(
  depthM: number,
  range: readonly [number, number],
): boolean {
  return Number.isFinite(depthM) && depthM >= range[0] && depthM <= range[1];
}
